import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { FiLock, FiCheck, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('validating'); // validating, valid, invalid, success
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const res = await api.get(`/users/reset-password/${token}`);
      setEmail(res.data.email);
      setStatus('valid');
    } catch (err) {
      setStatus('invalid');
      setError(err.response?.data?.message || 'Invalid or expired reset link');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/users/reset-password/${token}`, { newPassword });
      setStatus('success');
      toast.success('Password reset successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password');
    }
    setLoading(false);
  };

  // Validating state
  if (status === 'validating') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🔄</div>
            <p style={{ color: '#64748b' }}>Validating reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (status === 'invalid') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: 80, height: 80, borderRadius: '50%', 
              background: '#fef2f2', display: 'flex', alignItems: 'center', 
              justifyContent: 'center', margin: '0 auto 1.5rem' 
            }}>
              <FiAlertCircle size={40} color="#dc2626" />
            </div>
            <h2 style={{ margin: '0 0 0.5rem', color: '#dc2626', fontSize: '1.25rem' }}>
              Invalid Reset Link
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              {error || 'This password reset link is invalid or has expired.'}
            </p>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Please contact your Manager or Admin to generate a new reset link.
            </p>
            <button
              onClick={() => navigate('/')}
              style={buttonSecondaryStyle}
            >
              <FiArrowLeft size={16} />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              width: 80, height: 80, borderRadius: '50%', 
              background: '#f0fdf4', display: 'flex', alignItems: 'center', 
              justifyContent: 'center', margin: '0 auto 1.5rem' 
            }}>
              <FiCheck size={40} color="#16a34a" />
            </div>
            <h2 style={{ margin: '0 0 0.5rem', color: '#16a34a', fontSize: '1.25rem' }}>
              Password Reset Complete!
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              Your password has been successfully updated for <strong>{email}</strong>.
            </p>
            <button
              onClick={() => navigate('/')}
              style={buttonPrimaryStyle}
            >
              Continue to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Valid token - show reset form
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ 
            width: 60, height: 60, borderRadius: '50%', 
            background: '#EAF6FD', display: 'flex', alignItems: 'center', 
            justifyContent: 'center', margin: '0 auto 1rem' 
          }}>
            <FiLock size={28} color="#1272B3" />
          </div>
          <h2 style={{ margin: '0 0 0.25rem', color: '#0f172a', fontSize: '1.25rem', fontWeight: 800 }}>
            Reset Your Password
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.85rem', margin: 0 }}>
            for <strong>{email}</strong>
          </p>
        </div>

        {error && (
          <div style={errorStyle}>
            <FiAlertCircle size={16} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
              placeholder="Min 6 characters"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              placeholder="Repeat new password"
              required
              style={inputStyle}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={buttonPrimaryStyle}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.8rem', color: '#94a3b8' }}>
          Remember your password?{' '}
          <span 
            onClick={() => navigate('/')} 
            style={{ color: '#1272B3', cursor: 'pointer', fontWeight: 600 }}
          >
            Back to Login
          </span>
        </p>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #2C63AE 0%, #1272B3 50%, #1272B3 100%)',
  padding: '2rem'
};

const cardStyle = {
  background: '#fff',
  borderRadius: 20,
  padding: '2.5rem',
  width: '100%',
  maxWidth: 420,
  boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#374151',
  marginBottom: 6
};

const inputStyle = {
  width: '100%',
  padding: '0.85rem 1rem',
  fontSize: '0.95rem',
  border: '1px solid #d1d5db',
  borderRadius: 10,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s'
};

const buttonPrimaryStyle = {
  width: '100%',
  padding: '0.9rem',
  background: 'linear-gradient(135deg, #1272B3 0%, #1272B3 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: 10,
  fontWeight: 700,
  fontSize: '0.95rem',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8
};

const buttonSecondaryStyle = {
  ...buttonPrimaryStyle,
  background: '#f1f5f9',
  color: '#64748b',
  border: '1px solid #e2e8f0'
};

const errorStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0.85rem 1rem',
  background: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: 10,
  color: '#dc2626',
  fontSize: '0.85rem',
  fontWeight: 500,
  marginBottom: '1rem'
};

export default ResetPassword;
